import { usePodLabel } from "@app/components/assistant/conversation/tool_validation/usePodLabel";
import { useConversation } from "@app/hooks/conversations/useConversation";
import { parsePodConfigurationURI } from "@app/lib/actions/mcp_internal_actions/pod_configuration_uri";
import type { PodManagerEditInformationInput } from "@app/lib/api/actions/servers/pod_manager/types";
import { useSpaceInfo } from "@app/lib/swr/spaces";
import type { LightWorkspaceType } from "@app/types/user";
import { Chip } from "@dust-tt/sparkle";
import i18n from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

interface PodEditInformationValidationDetailsProps {
  input: PodManagerEditInformationInput;
  owner: LightWorkspaceType;
  conversationId?: string | null;
}

interface ChangeRowProps {
  label: string;
  before: string;
  after: string;
}

function formatAccess(access: "restricted" | "open"): string {
  return access === "open"
    ? i18n.t("conv.open", "Open")
    : i18n.t("conv.restricted", "Restricted");
}

function formatPinnedFramePath(path: string | null | undefined): string {
  if (path === null) {
    return i18n.t("pods.pinnedFrameUnpinned", "Unpinned");
  }
  if (path === undefined) {
    return "—";
  }
  return path;
}

function ChangeRow({ label, before, after }: ChangeRowProps) {
  const { t } = useTranslation();
  const hasChanged = before !== after;

  return (
    <div className="flex flex-col gap-1 px-3 py-2.5">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-muted-foreground">{before}</span>
        {hasChanged && (
          <>
            <span className="text-muted-foreground">→</span>
            <span className="font-medium text-foreground">{after}</span>
          </>
        )}
        {!hasChanged && (
          <Chip
            size="xs"
            color="primary"
            label={t("pods.podEditNoChange", "No change")}
          />
        )}
      </div>
    </div>
  );
}

export function PodEditInformationValidationDetails({
  input,
  owner,
  conversationId,
}: PodEditInformationValidationDetailsProps) {
  const { t } = useTranslation();
  const { podLabel, isPodLabelLoading } = usePodLabel({
    owner,
    dustPodUri: input.dustPod?.uri,
    conversationId,
  });

  const { conversation, isConversationLoading } = useConversation({
    workspaceId: owner.sId,
    conversationId: conversationId ?? null,
    options: { disabled: !conversationId },
  });

  const podSpaceId = useMemo(() => {
    if (input.dustPod?.uri) {
      const parsed = parsePodConfigurationURI(input.dustPod.uri);
      if (parsed.isOk()) {
        return parsed.value.podId;
      }
      return null;
    }
    return conversation?.spaceId ?? null;
  }, [conversation?.spaceId, input.dustPod?.uri]);

  const isWaitingForConversationSpaceId =
    !input.dustPod?.uri && !!conversationId && isConversationLoading;

  const { spaceInfo, isSpaceInfoLoading } = useSpaceInfo({
    workspaceId: owner.sId,
    spaceId: podSpaceId,
    disabled: !podSpaceId,
  });

  const isCurrentPodInfoLoading =
    isWaitingForConversationSpaceId ||
    (podSpaceId !== null && isSpaceInfoLoading && !spaceInfo);

  const currentAccess = spaceInfo
    ? spaceInfo.isRestricted
      ? "restricted"
      : "open"
    : null;

  const changes: ChangeRowProps[] = [];

  if (input.title !== undefined) {
    changes.push({
      label: t("pods.podEditFieldTitle", "Title"),
      before:
        spaceInfo?.name ??
        (isCurrentPodInfoLoading ? t("pods.loading", "Loading…") : "—"),
      after: input.title,
    });
  }

  if (input.description !== undefined) {
    changes.push({
      label: t("pods.description", "Description"),
      before:
        spaceInfo?.description ??
        (isCurrentPodInfoLoading ? t("pods.loading", "Loading…") : "—"),
      after: input.description,
    });
  }

  if (input.access !== undefined) {
    changes.push({
      label: t("conv.accessLabel", "Access"),
      before: currentAccess
        ? formatAccess(currentAccess)
        : isCurrentPodInfoLoading
          ? t("pods.loading", "Loading…")
          : "—",
      after: formatAccess(input.access),
    });
  }

  if (input.pinnedFramePath !== undefined) {
    changes.push({
      label: t("pods.podEditFieldPinnedFrame", "Pinned frame"),
      before: isCurrentPodInfoLoading
        ? t("pods.loading", "Loading…")
        : formatPinnedFramePath(spaceInfo?.pinnedFramePath ?? undefined),
      after: formatPinnedFramePath(input.pinnedFramePath),
    });
  }

  return (
    <div className="flex flex-col gap-3 pt-2">
      <p className="text-sm text-muted-foreground">
        {t(
          "pods.podEditInfoIntroPrefix",
          "The agent wants to update information for"
        )}{" "}
        <span className="font-medium text-foreground">
          {isPodLabelLoading ? t("pods.loading", "Loading…") : podLabel}
        </span>
        {t("pods.podEditInfoIntroSuffix", ".")}
      </p>

      {changes.length > 0 && (
        <div className="divide-y divide-separator overflow-hidden rounded-xl border border-separator bg-background">
          {changes.map((change) => (
            <ChangeRow key={change.label} {...change} />
          ))}
        </div>
      )}
    </div>
  );
}
